import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";
import { RoutesLink } from "@/utility/RouteLinks";
import { Plus, PencilIcon, Eye, DownloadCloud, Download } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { IProduct, IProductResponse } from "@/interfaces/Product/product.interface";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { useFilter } from "@/hooks/useFilter";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { GetFilterUrl, formatRand, showToastNotification, downloadExcel } from "@/utility/helper";
import Pagination from "@/components/common/Pagination";
import { RootState, useAppSelector } from "@/redux/store";
import { Switch } from "@/components/ui/switch";
import { CommonSelect } from "@/components/common/CommonSelect";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { TableItemIdentity } from "@/components/common/TableItemIdentity";
import { UpdateStockModal } from "@/components/common/Model/UpdateStockModal";
import { StarRating } from "@/components/common/StarRating";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import { ProductAdvancedFilters } from "@/components/common/Filters/ProductAdvancedFilters";
import Button from "@/components/common/Button";

export const Products = () => {
  const navigate = useRoleNavigate();
  const [products, setProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [stockUpdateProduct, setStockUpdateProduct] = useState<IProduct | null>(null);
  const { user } = useAppSelector((state: RootState) => state.user);
  const { canCreate, canEdit, canView, hasPermission, isAdmin, canExport } = usePermissions();
  const canDo = canEdit(RESOURCES.PRODUCT);
  const canImport = hasPermission(RESOURCES.PRODUCT, 'import-product');
  const canAddStock = hasPermission(RESOURCES.PRODUCT, 'add-stock');

  const { filters, debouncedFilters, updateFilter, handlePageChange, resetFilters, handlePageSizeChange } = useFilter<IFilter>({
    search: '',
    sort: '',
    page: 1,
    limit: 10,
    userId: isAdmin ? "" : user?.id,
    isGlobal: isAdmin ? "" : "0",
  });

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);

  const handleDateChange = (newRange: any) => {
    const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
    setDateRange(resolvedRange);

    const start = resolvedRange[0]?.startDate;
    const end = resolvedRange[0]?.endDate;
    const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
    const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

    if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
      updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
    }
  };

  const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
    limit: 10,
    page: 1,
    total: 0,
    totalPages: 0,
  });

  const lastParamsRef = useRef<string>('');

  const fetchProducts = async () => {
    const currentParams = JSON.stringify(debouncedFilters);
    if (currentParams === lastParamsRef.current) return;
    lastParamsRef.current = currentParams;

    setLoading(true);
    try {
      const queryParams = GetFilterUrl(debouncedFilters);
      const response = await callApi<IProductResponse>(
        `${API_URL + API_ENDPOINT.products}?${queryParams}`,
        { method: 'GET' }
      );
      setProducts(response?.data?.products || []);
      setPageMetadata(
        response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 }
      );
    } catch (error) {
      ThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    const filterParems = {
      startDate: debouncedFilters.startDate,
      endDate: debouncedFilters.endDate,
      isGlobal: debouncedFilters.isGlobal,
      sort: debouncedFilters.sort,
      search: debouncedFilters.search,
      minPrice: debouncedFilters.minPrice,
      maxPrice: debouncedFilters.maxPrice,
      rating: debouncedFilters.rating,
      tagId: debouncedFilters.tagId,
      categoryIds: debouncedFilters.categoryIds,
      subCategoryId: debouncedFilters.subCategoryId,
      storeId: debouncedFilters.storeId,
      status: debouncedFilters.status,
    };
    const queryParams = GetFilterUrl(filterParems);
    const url = `${API_URL + API_ENDPOINT.products}/export/excel?${queryParams}`;
    setLoadingExport(true);
    try {
      await downloadExcel(url, 'products_export.xlsx');
    } catch (error) {
      ThrowError(error);
    } finally {
      setLoadingExport(false);
    }
  };

  const handleStatusToggle = async (id: string, currentStatus: string) => {
    setUpdatingId(id);
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const response = await callApi<any>(`${API_URL}${API_ENDPOINT.products}/${id}`, {
        method: 'PATCH',
        data: { status: newStatus }
      });
      showToastNotification("success", response?.message || "Product status updated successfully");
      setProducts(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p));
    } catch (error) {
      ThrowError(error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStockUpdateSuccess = (productId: string, newStock: number) => {
    console.log("Success:", productId, newStock);
    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        return {
          ...p,
          productInfo: {
            ...p.productInfo,
            stockQuantity: newStock
          }
        };
      }
      return p;
    }));
  };

  useEffect(() => {
    fetchProducts();
  }, [
    debouncedFilters.page,
    debouncedFilters.limit,
    debouncedFilters.search,
    debouncedFilters.sort,
    debouncedFilters.isGlobal,
    debouncedFilters.minPrice,
    debouncedFilters.maxPrice,
    debouncedFilters.rating,
    debouncedFilters.tagId,
    debouncedFilters.categoryIds,
    debouncedFilters.subCategoryId,
    debouncedFilters.storeId,
    debouncedFilters.status,
    debouncedFilters.startDate,
    debouncedFilters.endDate
  ]);

  const columns: TableColumn<IProduct>[] = [
    {
      key: "image",
      header: "Product",
      render: (item) => (
        <TableItemIdentity
          image={item.media && item.media[0]?.url}
          name={item.name}
          // subtitle={item.id.slice(-8)}
          fallbackText="No Img"
        />
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (item) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">{item.category?.name || '---'}</span>
          <span className="text-[10px] text-muted-foreground">{item.subCategory?.name || '---'}</span>
        </div>
      )
    },
    {
      key: "price",
      header: "Price",
      render: (item) => (
        <div className="font-semibold text-primary">
          {formatRand(item.price)}
        </div>
      )
    },
    {
      key: "rating",
      header: "Rating",
      render: (item) => (
        <div className="flex flex-col gap-1">
          <StarRating
            rating={Number(item.avgRating) || 0}
            starSize="w-3 h-3"
          />
          <span className="text-[10px] text-muted-foreground font-medium">
            ({item.ratingCount || 0} Reviews)
          </span>
        </div>
      )
    },
    {
      key: "stockQuantity",
      header: "Stock",
      render: (item) => (
        <div className="flex flex-col">
          <span className={`font-medium ${item.productInfo?.stockQuantity <= 5 ? 'text-destructive' : 'text-foreground'}`}>
            {item.productInfo?.stockQuantity || 0} Units
          </span>
          {item.productInfo?.stockQuantity <= 5 && (
            <span className="text-[9px] text-destructive font-bold uppercase">Low Stock</span>
          )}
        </div>
      )
    },
    {
      key: "isGlobel",
      header: "Product Source",
      render: (item) => (
        <div className="flex flex-col">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full w-fit ${item.isGlobal
            ? 'bg-blue-100 text-blue-700 border border-blue-200'
            : 'bg-amber-100 text-amber-700 border border-amber-200'
            }`}>
            {item.isGlobal ? "Admin Product" : "Store Product"}
          </span>
        </div>
      )
    },
    {
      key: "status",
      header: "Status",
      render: (item) => {
        // Only owner or admin can toggle status
        const canToggle = canDo;

        return (
          <div className="flex items-center gap-3">
            <Switch
              checked={item.status === 'active'}
              onCheckedChange={() => handleStatusToggle(item.id, item.status)}
              disabled={updatingId === item.id || !canToggle || !canDo}
              title={!canToggle ? "You don't have permission to toggle this product's status" : !canDo ? "Role permission required" : "Toggle status"}
            />
            <StatusBadge status={item.status} />
          </div>
        );
      }
    }
  ];


  const getActions = (_record: IProduct): Action<IProduct>[] => [
    ...(canView(RESOURCES.PRODUCT) ? [{
      text: "View",
      icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer" />,
      onClick: (record) => {
        navigate(RoutesLink.ViewProduct.replace(':id', record.id));
      }
    }] : []),
    ...(canDo ? [{
      text: "Edit",
      icon: <PencilIcon size={16} strokeWidth={1.5} className="cursor-pointer" />,
      onClick: (record: IProduct) => {
        navigate(RoutesLink.EditProduct.replace(':id', record.id));
      }
    }] : []),
    ...(canAddStock ? [{
      text: "Add Stock",
      icon: <Plus size={16} strokeWidth={1.5} className="cursor-pointer" />,
      onClick: (record: IProduct) => {
        setStockUpdateProduct(record);
      }
    }] : [])
  ];

  return (
    <Layout>
      <MainHeading
        title="Products"
        description={`${pageMetadata.total} Products Total · ${products.length} items on current page`}
        actionLabel={canImport && !isAdmin && "Import Products"}
        actionIcon={canImport && !isAdmin && <DownloadCloud className="w-4 h-4" />}
        onAction={() => {
          navigate(RoutesLink.ImportProducts);
        }}
      >
        {canCreate(RESOURCES.PRODUCT) && (
          <button
            onClick={() => navigate(RoutesLink.AddProduct)}
            className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-sm text-sm font-bold hover:bg-primary/20 transition-all text-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        )}
      </MainHeading>

      <div className="mt-6 flex flex-col gap-6">
        <ProductAdvancedFilters
          filters={filters}
          updateFilter={updateFilter}
          resetFilters={resetFilters}
        />

        <div className="flex items-center justify-end gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={handleDateChange as any}
            />
            {isAdmin && (
              <CommonSelect
                value={filters.isGlobal}
                onValueChange={(val) => updateFilter({ isGlobal: val })}
                options={[
                  // { value: "", label: "All Sources" },
                  { value: "1", label: "Admin Products" },
                  { value: "0", label: "Store Products" },
                ]}
                placeholder="Select Product Source"
                triggerClassName="w-[160px] h-10 rounded-xl border-secondary/20 bg-card font-semibold"
              />
            )}
            <CommonSelect
              value={filters.sort}
              onValueChange={(val) => updateFilter({ sort: val })}
              options={[
                { value: "newest", label: "Newest First" },
                { value: "oldest", label: "Oldest First" },
              ]}
              triggerClassName="w-[160px] h-10 rounded-xl border-secondary/20 bg-card font-semibold"
              placeholder="Sort By"
            />
            {canExport(RESOURCES.PRODUCT) && <Button
              variant="outline"
              onClick={() => handleExport()}
              className="h-10 px-4 flex items-center gap-2 rounded-xl"
              leftIcon={<Download className="w-4 h-4" />}
              isLoading={loadingExport}
            >
              Export
            </Button>}
          </div>
        </div>
        <CommonTable
          columns={columns}
          data={products}
          loading={loading}
          emptyMessage="No products found in your catalog. Try importing some!"
          actions={getActions}
        />

        <Pagination
          currentPage={filters.page || 1}
          totalPages={pageMetadata.totalPages}
          totalItems={pageMetadata.total}
          itemsPerPage={pageMetadata.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <UpdateStockModal
        isOpen={!!stockUpdateProduct}
        product={stockUpdateProduct}
        onClose={() => setStockUpdateProduct(null)}
        onSuccess={handleStockUpdateSuccess}
      />
    </Layout>
  );
};

